import {
  Grid,
  MenuItem,
  Typography,
  FormControl,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableHead,
  Box,
  FormHelperText,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  LinearProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import UserAvatar from "../../common/UserAvatar";
import React, { useState, useEffect, useRef } from "react";
import { StyledSelect, StyledTextField } from "../../customComponents/styled/styledInputs";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { useFormik } from "formik";
import { getApiBaseUrl } from "../../../helpers/apiUrl";
import * as yup from "yup";
import { useGetClassesPaginatedQuery } from "../classroom/classApiSlice";
import { useGetSessionsPaginatedQuery, useGetCurrentTermQuery } from "../settings/session/sessionApiSlice";
import { useGetTemplatesPaginatedQuery } from "../Templates/templatesApiSlice";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import toast from "../../../helpers/toast";
import { useCreateStudentReportMutation, useLazyFetchStudentReportsQuery, useDeleteStudentReportMutation, useGetReportStatusQuery, useCancelReportMutation } from "./studentReportApiSlice";
import { PictureAsPdf, Visibility, Close as CloseIcon, Refresh as RefreshIcon, Delete as DeleteIcon } from "@mui/icons-material";

const validationSchema = yup.object({
  class: yup.object().required("Required"),
  regNumber: yup.string().required("Required"),
  session: yup.object().required("Required"),
  term: yup.string().required("Required"),
});

const ReportPreviewModal = ({ url, open, onClose }) => {
  const [iframeLoading, setIframeLoading] = useState(true);
  const googleDocsUrl = url
    ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
    : null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: "90vh" } }}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        Report Preview
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: "hidden", position: "relative", bgcolor: "#fff" }}>
        {iframeLoading && googleDocsUrl && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.8)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {googleDocsUrl && (
          <iframe
            key={url}
            src={googleDocsUrl}
            title="Report Preview"
            width="100%"
            height="100%"
            style={{ border: "none", backgroundColor: "#fff" }}
            onLoad={() => setIframeLoading(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

function StudentReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [studentReports, setStudentReports] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [reportTaskId, setReportTaskId] = useState(null);
  const [reportTaskDone, setReportTaskDone] = useState(false);
  const [reportProgress, setReportProgress] = useState(null);
  const pendingParamsRef = useRef(null);
  const { data: currentTerm } = useGetCurrentTermQuery();

  const isPolling = reportTaskId && !reportTaskDone;
  const { data: reportStatusData } = useGetReportStatusQuery(reportTaskId, {
    skip: !reportTaskId || reportTaskDone,
    pollingInterval: isPolling ? 3000 : 0,
  });

  useEffect(() => {
    if (!reportStatusData) return;
    const { state, info } = reportStatusData;
    if (state === 'PROGRESS' && info) {
      setReportProgress(info);
    } else if (state === 'SUCCESS') {
      setReportTaskDone(true);
      setReportProgress(null);
      toast.success('Report generated successfully');
      if (pendingParamsRef.current) {
        const { student, params } = pendingParamsRef.current;
        fetchStudentReports(params).then(({ data: reports }) => {
          if (reports && reports.length > 0) {
            setStudentReports(reports.map(r => ({ student, report: r })));
          }
        });
        pendingParamsRef.current = null;
      }
      setReportTaskId(null);
    } else if (state === 'FAILURE') {
      setReportTaskDone(true);
      setReportProgress(null);
      const errMsg = typeof info === 'string' ? info : 'Report generation failed';
      toast.error(errMsg);
      setReportTaskId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportStatusData]);

  const [createStudentReport, { isLoading: isCreatingStudentReport }] =
    useCreateStudentReportMutation();
  const [fetchStudentReports, { isFetching: isFetchingStudentReports }] = useLazyFetchStudentReportsQuery();
  const [deleteStudentReport] = useDeleteStudentReportMutation();
  const [cancelReport, { isLoading: isCancelling }] = useCancelReportMutation();

  const handleCancel = async () => {
    if (!reportTaskId) return;
    try {
      await cancelReport(reportTaskId).unwrap();
      setReportTaskId(null);
      setReportTaskDone(true);
      setReportProgress(null);
      pendingParamsRef.current = null;
      toast.info('Report generation cancelled.');
    } catch {
      toast.error('Failed to cancel.');
    }
  };

  const handleDelete = async (item) => {
    const willDelete = window.confirm("This will permanently delete this report.");
    if (willDelete) {
      try {
        await deleteStudentReport({ id: item.report?.id }).unwrap();
        setStudentReports(prev => prev.filter(r => r.report?.id !== item.report?.id));
        toast.success("Report has been deleted.");
      } catch (error) {
        toast.error(error?.message || "Failed to delete report");
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      class: "",
      regNumber: "",
      session: "",
      term: "",
      template: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const BASE_URL = getApiBaseUrl();
        const token = localStorage
          .getItem("access_token")
          .replace('"', "")
          .replace('"', "");

        const studentRes = await fetch(
          `${BASE_URL}/student/by-reg-number?reg_number=${values.regNumber}&classroom_id=${values.class.id}&session_id=${values.session.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!studentRes.ok) {
          toast.error("Student not found with this registration number in the selected class");
          return;
        }

        const foundStudent = await studentRes.json();

        if (!foundStudent) {
          toast.error("Student not found with this registration number");
          return;
        }

        if (!values.template) {
          const { data: reports, error } = await fetchStudentReports({ student_id: foundStudent.id, classroom_id: values.class.id, term_id: values.term });
          if (error || !reports || reports.length === 0) {
            toast.info("No reports found for this student, class, and term. Please select a template to generate one.");
            return;
          }
          setStudentReports(reports.map(r => ({ student: foundStudent, report: r })));
          return;
        }

        const body = {
          student_id: foundStudent.id,
          term_id: values.term,
          classroom_id: values.class.id,
          template_id: values.template,
          regenerate: isRegenerating,
        };
        setIsRegenerating(false);

        const response = await createStudentReport(body).unwrap();
        if (response.id) {
          setStudentReports([{ student: foundStudent, report: response }]);
          toast.success(`${foundStudent.first_name} ${foundStudent.last_name}'s report is generated successfully`);
        } else if (response.task_id) {
          pendingParamsRef.current = {
            student: foundStudent,
            params: { student_id: foundStudent.id, classroom_id: values.class.id, term_id: values.term },
          };
          setReportTaskDone(false);
          setReportTaskId(response.task_id);
          toast.info('Generating report...');
        } else {
          toast.success("Generating report");
        }
      } catch (error) {
        toast.error(error?.message || error?.message || "something went wrong");
      }
    },
  });

  const handlePreview = (url) => {
    setPreviewUrl(url);
  };

  const [classroomSearch, setClassroomSearch] = useState("");
  const { data: classroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 10, q: classroomSearch });
  const classrooms = classroomsData?.classrooms ?? [];
  const [sessionSearch, setSessionSearch] = React.useState("");
  const { data: sessionsData } = useGetSessionsPaginatedQuery({ page: 1, per_page: 5, q: sessionSearch });
  const sessions = sessionsData?.sessions ?? [];
  const { data: templatesData } = useGetTemplatesPaginatedQuery({ page: 1, per_page: 100 });
  const templates = (templatesData?.templates ?? []).filter(t => t.type !== 'broadsheet');

  // Auto-select current term when session changes
  React.useEffect(() => {
    const terms = formik.values.session?.terms;
    if (!terms || !currentTerm) return;
    const matchingTerm = terms.find(t => t.id === currentTerm.id);
    formik.setFieldValue("term", matchingTerm ? matchingTerm.id : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.session?.id, currentTerm?.id]);

  return (
    <div style={{ padding: "0 10px" }}>
      <Grid container columnSpacing={4}>
        <Grid item xs={12} md={4}>
          <StyledPaper sx={{ padding: "15px" }}>
            <Typography variant="formHeading" sx={{ marginBottom: "20px" }}>
              Student Report Download Form
            </Typography>

            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container rowSpacing={3}>
                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.class && formik.errors.class}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">CLASS</Typography>
                    <Autocomplete
                      options={classrooms}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={formik.values.class || null}
                      onChange={(_, value) => formik.setFieldValue("class", value || "")}
                      onInputChange={(_, value, reason) => { if (reason === "input") setClassroomSearch(value); }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search class..."
                          error={formik.touched.class && Boolean(formik.errors.class)}
                        />
                      )}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.class && formik.errors.class}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.regNumber && formik.errors.regNumber}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">STUDENT REG. NUMBER</Typography>
                    <StyledTextField
                      id="regNumber"
                      name="regNumber"
                      placeholder="Enter student registration number"
                      value={formik.values.regNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.regNumber && Boolean(formik.errors.regNumber)}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.regNumber && formik.errors.regNumber}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.session && formik.errors.session}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">SESSION</Typography>
                    <Autocomplete
                      options={sessions}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={formik.values.session || null}
                      onChange={(_, value) => formik.setFieldValue("session", value || "")}
                      onInputChange={(_, value, reason) => {
                        if (reason === "input") setSessionSearch(value);
                      }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search session..."
                          error={formik.touched.session && Boolean(formik.errors.session)}
                        />
                      )}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.session && formik.errors.session}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.term && formik.errors.term}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">TERM</Typography>
                    <StyledSelect
                      displayEmpty
                      id="term"
                      name="term"
                      onChange={formik.handleChange}
                      value={formik.values.term}
                      error={formik.touched.term && Boolean(formik.errors.term)}
                    >
                      <MenuItem value="" disabled>
                        <em>Select a term</em>
                      </MenuItem>
                      {formik.values.session?.terms?.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}{currentTerm?.id === item.id ? " (Current)" : ""}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    <FormHelperText variant="error">
                      {formik.touched.term && formik.errors.term}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.template && formik.errors.template}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">TEMPLATE</Typography>
                    <StyledSelect
                      id="template"
                      name="template"
                      value={formik.values.template}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.template &&
                        Boolean(formik.errors.template)
                      }
                    >
                      <MenuItem value="">
                        <em>No template (all)</em>
                      </MenuItem>
                      {templates.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    <FormHelperText variant="error">
                      {formik.touched.template && formik.errors.template}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12} textAlign={"right"}>
                  <StyledLoadingButton
                    loading={isCreatingStudentReport || isFetchingStudentReports || isPolling}
                    disabled={isPolling}
                    type="submit"
                    sx={{ textTransform: "none" }}
                  >
                    {isPolling ? 'Generating...' : 'Get Report'}
                  </StyledLoadingButton>
                </Grid>
              </Grid>
            </Box>
          </StyledPaper>
        </Grid>

        {/* Generated Table */}

        <Grid item xs={12} md={8}>
          <StyledPaper sx={{ paddingBottom: "25px" }}>
            <Box sx={{ padding: "15px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="formHeading">Student Report</Typography>
                <Typography
                  variant="formSubHeading"
                  sx={{ textTransform: "none" }}
                >
                  Select student to view report
                </Typography>
              </Box>
              {studentReports.length > 0 && (
                <Tooltip title="Regenerate report with current settings">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      setIsRegenerating(true);
                      formik.submitForm();
                    }}
                    disabled={isCreatingStudentReport || isFetchingStudentReports}
                    sx={{ textTransform: "none" }}
                  >
                    Regenerate
                  </Button>
                </Tooltip>
              )}
            </Box>
            <TableContainer sx={{ overflowX: "auto" }}>
              {isPolling && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      {reportProgress?.status || 'Generating report...'}
                    </Typography>
                  </Box>
                  {reportProgress?.percent != null && (
                    <LinearProgress variant="determinate" value={reportProgress.percent} />
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={isCancelling}
                      sx={{ textTransform: 'none' }}
                    >
                      {isCancelling ? 'Cancelling...' : 'Cancel'}
                    </Button>
                  </Box>
                </Box>
              )}
              {isMobile ? (
                <Box sx={{ px: 2, pb: 1 }}>
                  {studentReports.map((item, index) => (
                    <Box key={item.report?.id || index} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                        <UserAvatar
                          src={item.student?.image}
                          name={`${item.student?.first_name || ""} ${item.student?.last_name || ""}`.trim()}
                        />
                        <Typography sx={{ fontWeight: 600 }}>
                          {item.student.first_name} {item.student.last_name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">Reg No: {item.student.reg_number}</Typography>
                      <Typography variant="body2" color="text.secondary">Term: {item.report?.term?.name || "—"}</Typography>
                      <Typography variant="body2" color="text.secondary">Session: {item.report?.session?.name || "—"}</Typography>
                      <Typography variant="body2" color="text.secondary">Template: {item.report?.template?.name || "—"}</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                        {item.report.file_url ? (
                          <>
                            <Tooltip title="Preview report">
                              <IconButton
                                size="small"
                                onClick={() => handlePreview(item.report.file_url)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download report">
                              <IconButton
                                size="small"
                                component="a"
                                href={item.report.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={`${item.student.first_name} ${item.student.last_name}.pdf`}
                              >
                                <PictureAsPdf fontSize="small" style={{ color: "red" }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : item.report.requires_parent_payment ? (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            PDF locked — awaiting parent payment
                          </Typography>
                        ) : null}
                        <Tooltip title="Delete report">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(item)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "2px solid lightgrey " }}>
                      <StyledTableCell>PHOTO</StyledTableCell>
                      <StyledTableCell>REG. NO</StyledTableCell>
                      <StyledTableCell>NAME</StyledTableCell>
                      <StyledTableCell>TERM</StyledTableCell>
                      <StyledTableCell>SESSION</StyledTableCell>
                      <StyledTableCell>TEMPLATE</StyledTableCell>
                      <StyledTableCell>ACTIONS</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {studentReports.map((item, index) => (
                      <StyledTableRow key={item.report?.id || index}>
                        <StyledTableCell>
                          <UserAvatar
                            src={item.student?.image}
                            name={`${item.student?.first_name || ""} ${item.student?.last_name || ""}`.trim()}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          {item.student.reg_number}
                        </StyledTableCell>
                        <StyledTableCell>
                          {item.student.first_name}{" "}
                          {item.student.last_name}
                        </StyledTableCell>
                        <StyledTableCell>
                          {item.report?.term?.name || "—"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {item.report?.session?.name || "—"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {item.report?.template?.name || "—"}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                            {item.report.file_url ? (
                              <>
                                <Tooltip title="Preview report">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePreview(item.report.file_url)}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download report">
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={item.report.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download={`${item.student.first_name} ${item.student.last_name}.pdf`}
                                  >
                                    <PictureAsPdf fontSize="small" style={{ color: "red" }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : item.report.requires_parent_payment ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                PDF locked — awaiting parent payment
                              </Typography>
                            ) : null}
                            <Tooltip title="Delete report">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(item)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </StyledPaper>
        </Grid>
      </Grid>
      <ReportPreviewModal
        url={previewUrl}
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
      />
    </div>
  );
}

export default StudentReport;
